/**
 * MerakiApiSuitePage — categorized list of runnable Meraki operations.
 *
 * The legacy template is "your all-in-one tool to manage Meraki functionality".
 * In React it becomes a category-grouped, filterable list with a destructiveness
 * indicator on each row + a quick-run CTA. Per-operation execution screens are
 * stub navigations for now (placeholder route).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Search,
  Sparkles,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardSub,
  CardTitle,
} from "@/components/ui/card";
import { CardGridSkeleton } from "@/components/ui/skeleton";
import { MerakiSubNav } from "@/components/app/meraki-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useMerakiApiSuite } from "@/stores/meraki-api-suite";
import type { ApiOperation } from "@/api/schemas/meraki";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

/** Bucket destructiveness 1-5 into pills + colors. */
function risk(d: number): { variant: "ok" | "warn" | "err"; label: string } {
  if (d <= 1) return { variant: "ok", label: "read-only" };
  if (d <= 3) return { variant: "warn", label: "writes config" };
  return { variant: "err", label: "high impact" };
}

/** Format an ISO timestamp as "3d ago" for compact display. */
function timeAgo(iso?: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function MerakiApiSuitePage() {
  const animatedRootRef = useUiAutoAnimate();

  const { data, status, error, load } = useMerakiApiSuite();
  useEffect(() => {
    void load();
  }, [load]);

  const [query, setQuery] = useState("");
  const [showRiskyOnly, setShowRiskyOnly] = useState(false);

  // Group + filter — memoized so re-keying doesn't walk the array unnecessarily.
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = data.filter((op) => {
      if (showRiskyOnly && op.destructiveness < 4) return false;
      if (!q) return true;
      return (
        op.name.toLowerCase().includes(q) ||
        op.description.toLowerCase().includes(q) ||
        op.slug.toLowerCase().includes(q)
      );
    });
    const out: Record<string, ApiOperation[]> = {};
    for (const op of filtered) (out[op.category] ??= []).push(op);
    return out;
  }, [data, query, showRiskyOnly]);

  const totalShown = Object.values(grouped).reduce(
    (n, list) => n + list.length,
    0,
  );

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/meraki/provisioning" className="text-fg-dim hover:text-fg">
          Meraki
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">API suite</span>
      </div>

      <MerakiSubNav />

      <NoVendorKeysBanner vendor="meraki" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            API suite
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Every Meraki dashboard operation Opus knows how to run, grouped by
            category. Read-only by default — high-impact actions are marked and
            require a confirmation step.
          </p>
        </div>
        <Badge variant="info" size="md">
          <Sparkles size={11} />
          {data.length} operations available
        </Badge>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search
            size={14}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name, slug, description…"
            className="pl-9"
          />
        </div>
        <Button
          variant={showRiskyOnly ? "primary" : "ghost"}
          size="sm"
          onClick={() => setShowRiskyOnly((v) => !v)}
        >
          <Shield size={13} />
          {showRiskyOnly ? "Risky only · on" : "Risky only · off"}
        </Button>
        <span className="ml-auto font-mono text-xs text-fg-mute">
          {totalShown}/{data.length} shown
        </span>
      </div>

      {/* Grouped list */}
      <div className="flex flex-col gap-5">
        {Object.entries(grouped).map(([category, ops]) => (
          <Card key={category}>
            <CardHeader>
              <div>
                <CardTitle>{category}</CardTitle>
                <CardSub>
                  {ops.length} operation{ops.length === 1 ? "" : "s"}
                </CardSub>
              </div>
              <Badge variant="neutral" size="sm">
                {ops.length}
              </Badge>
            </CardHeader>
            <ul className="m-0 flex list-none flex-col p-0">
              {ops.map((op) => {
                const r = risk(op.destructiveness);
                return (
                  <li
                    key={op.slug}
                    className={cn(
                      "grid grid-cols-[1fr_auto_auto] items-center gap-4 border-t border-line/60 px-5 py-4",
                      "first:border-t-0",
                    )}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="truncate text-sm font-semibold text-fg">
                          {op.name}
                        </div>
                        <Badge variant={r.variant} size="sm">
                          {r.label}
                        </Badge>
                      </div>
                      <div className="mt-1 max-w-[640px] text-xs text-fg-dim">
                        {op.description}
                      </div>
                      <div className="mt-1 flex gap-3 font-mono text-[10.5px] text-fg-mute">
                        <span>{op.slug}</span>
                        <span>· last run {timeAgo(op.lastRunAt)}</span>
                      </div>
                    </div>
                    <Button
                      asChild
                      size="sm"
                      variant={op.destructiveness >= 4 ? "ghost" : "primary"}
                    >
                      <Link to={`/meraki/api-suite/run/${op.slug}`}>
                        Run
                        <ArrowRight size={13} />
                      </Link>
                    </Button>
                    <span className="font-mono text-[10px] text-fg-mute">
                      d{op.destructiveness}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>
        ))}
        {totalShown === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-fg-mute">
              {query
                ? `No operations match "${query}".`
                : "No operations available."}
            </CardContent>
          </Card>
        ) : null}
      </div>

      {status === "loading" && data.length === 0 ? (
        <CardGridSkeleton cards={4} />
      ) : null}
    </div>
  );
}
